import { extendTheme, type ThemeConfig, type ThemeOverride } from '@chakra-ui/react';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

const semanticTokens: ThemeOverride['semanticTokens'] = {
  colors: {
    'text.900': {
      default: '#0F172A',
      _dark: '#F8FAFC',
    },
    'text.600': {
      default: '#475569',
      _dark: '#CBD5F5',
    },
    surface: {
      default: '#FFFFFF',
      _dark: '#1F2937',
    },
    elevated: {
      default: '#F8FAFC',
      _dark: '#111827',
    },
    success: {
      default: '#16A34A',
      _dark: '#4ADE80',
    },
    warning: {
      default: '#D97706',
      _dark: '#FBBF24',
    },
    danger: {
      default: '#DC2626',
      _dark: '#F87171',
    },
  },
};

const theme = extendTheme({
  config,
  semanticTokens,
  colors: {
    brand: {
      50: '#E8F0FF',
      100: '#C6D8FF',
      200: '#A0BEFF',
      300: '#7AA3FF',
      400: '#5588FF',
      500: '#2563EB',
      600: '#1D4ED8',
      700: '#1E40AF',
      800: '#1E3A8A',
      900: '#1E3A8A',
    },
    success: {
      50: '#ECFDF5',
      100: '#D1FAE5',
      200: '#A7F3D0',
      300: '#6EE7B7',
      400: '#34D399',
      500: '#10B981',
      600: '#059669',
      700: '#047857',
      800: '#065F46',
      900: '#064E3B',
    },
    warning: {
      50: '#FEFCE8',
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24',
      500: '#F59E0B',
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
    },
    danger: {
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F87171',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D',
    },
  },
  radii: {
    none: '0',
    sm: '4px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
  },
  shadows: {
    sm: '0px 12px 30px -12px rgba(15, 23, 42, 0.25)',
    outline: '0 0 0 3px rgba(37, 99, 235, 0.45)',
  },
  fonts: {
    heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  space: {
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: 'lg',
        fontWeight: 'semibold',
        _focusVisible: {
          boxShadow: 'outline',
        },
      },
      variants: {
        solid: {
          bg: 'brand.500',
          color: 'white',
          _hover: { bg: 'brand.600' },
          _active: { bg: 'brand.700' },
          _disabled: { bg: 'brand.200', cursor: 'not-allowed' },
        },
        ghost: {
          bg: 'transparent',
          color: 'text.900',
          border: '1px solid',
          borderColor: 'gray.200',
          _hover: { bg: 'gray.100' },
          _active: { bg: 'gray.200' },
          _dark: {
            color: 'text.600',
            borderColor: 'gray.700',
            _hover: { bg: 'gray.800' },
            _active: { bg: 'gray.700' },
          },
        },
        subtle: {
          bg: 'brand.50',
          color: 'brand.600',
          _hover: { bg: 'brand.100' },
          _active: { bg: 'brand.200' },
        },
      },
      defaultProps: {
        variant: 'solid',
        colorScheme: 'brand',
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: 'lg',
          bg: 'surface',
          boxShadow: 'sm',
        },
      },
      variants: {
        elevated: {
          container: {
            bg: 'elevated',
            boxShadow: 'sm',
          },
        },
        outline: {
          container: {
            borderWidth: '1px',
            borderColor: 'gray.200',
          },
        },
      },
    },
    Tag: {
      baseStyle: {
        borderRadius: 'full',
        fontWeight: 'medium',
        px: 3,
        py: 1,
      },
      variants: {
        success: {
          bg: 'success.50',
          color: 'success.700',
        },
        warning: {
          bg: 'warning.50',
          color: 'warning.700',
        },
        danger: {
          bg: 'danger.50',
          color: 'danger.700',
        },
        brand: {
          bg: 'brand.50',
          color: 'brand.600',
        },
      },
    },
  },
});

export default theme;
